import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import './HowItWorks.css';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

/* ================================================================
   STEP DATA -- single source of truth for every section
   ================================================================ */
const steps = [
  {
    id: 'build',
    num: '01',
    name: 'Build',
    descriptor: 'Plain-English authoring',
    title: 'Build Your Semantic Domain Layer',
    tagline: 'Write your rules in plain English. We compile them into a precise cognitive layer.',
    intro:
      'No markup. No code. No ontology builder. You describe the rules, policies, exceptions, and reasoning patterns that drive your business in natural language, and the Linguistic Cognitive Engine compiles them into structured semantic models that range from fully deterministic logic to multidimensional probabilistic reasoning.',
    whyItMatters:
      'You get the foundational enterprise brain \u2014 your entire domain represented with both precision and cognitive flexibility.',
    chipGroups: [
      {
        label: 'You express, in plain English',
        items: [
          'rules', 'policies', 'constraints', 'exceptions',
          'relationships', 'workflows', 'expert heuristics', 'reasoning patterns'
        ],
        variant: 'chip'
      },
      {
        label: 'The engine compiles it into',
        items: ['Deterministic logic', 'Probabilistic models'],
        variant: 'pill'
      }
    ],
    special: null
  },
  {
    id: 'connect',
    num: '02',
    name: 'Connect',
    descriptor: 'Append data, merge silos',
    title: 'Append Unlimited Data \u2014 and Merge Silos Automatically',
    tagline: 'One semantic layer. Infinite data. One unified model.',
    intro:
      'Once the semantic domain layer exists, you attach any volume of enterprise data \u2014 documents, knowledge bases, transactions, EHRs, claims, ledgers, CRM history, policy PDFs, runtime facts. The semantic layer aligns concepts, merges overlapping structures, resolves contradictions, and links meaning across every department on its own.',
    whyItMatters:
      'You end up with one unified cognitive model of the entire enterprise, no matter how many systems you started with.',
    chipGroups: [
      {
        label: 'Attach any data source',
        items: [
          'knowledge bases', 'documents', 'transaction logs', 'operational events',
          'EHRs / claims / ledgers', 'CRM & ticketing history', 'policy PDFs',
          'spreadsheets', 'runtime facts', 'system metadata'
        ],
        variant: 'chip'
      },
      {
        label: 'Automatic, no plumbing',
        items: [
          'Aligns concepts', 'Merges overlapping structures', 'Resolves contradictions',
          'Links across departments', 'Joins fragmented knowledge'
        ],
        variant: 'pill'
      }
    ],
    special: null
  },
  {
    id: 'live',
    num: '03',
    name: 'Live',
    descriptor: 'Real-time model updates',
    title: 'Connect Live System Data for Continuous, Real-Time Updates',
    tagline: 'Self-updating models \u2014 with no retraining cycles.',
    intro:
      'Traditional ML models go stale the moment the world changes. CerebralMachines streams live system data straight into the semantic model, so probabilistic components update in real time and new evidence instantly influences reasoning \u2014 with none of the downtime, cost, or brittleness of retraining a model.',
    whyItMatters:
      'You now have up-to-the-second cognitive models that evolve automatically with your business.',
    chipGroups: [
      {
        label: 'Updated in real time',
        items: ['Probabilistic updates', 'Live evidence', 'Continuous influence on reasoning'],
        variant: 'pill'
      },
      {
        label: 'What you stop doing',
        items: ['No fine-tuning', 'No retraining', 'No redeployment', 'No manual engineering'],
        variant: 'negative'
      }
    ],
    special: null
  },
  {
    id: 'invoke',
    num: '04',
    name: 'Invoke',
    descriptor: 'Reasoning as an API',
    title: 'Invoke Cognitive Models from Agents, Apps, and Automations',
    tagline: 'Call the semantic engine like an API \u2014 receive precise, human-grade reasoning.',
    intro:
      'Your agents, applications, workflows, and decision systems query the cognitive graph for decisions, classifications, judgments, predictions, and multi-step reasoning. Unlike LLMs, every answer is deterministic when required, probabilistic when uncertainty exists, and grounded in your domain logic \u2014 not pattern-matched from training data.',
    whyItMatters:
      'This is real enterprise reasoning, not sentence prediction.',
    chipGroups: [
      {
        label: 'Call the engine for',
        items: [
          'decisions', 'classifications', 'judgments', 'interpretations',
          'predictions', 'multi-step reasoning', 'evidence-backed justifications',
          'cross-domain conclusions', 'recommendations'
        ],
        variant: 'chip'
      },
      {
        label: 'Guaranteed on every answer',
        items: ['deterministic', 'probabilistic', 'explainable', 'consistent', 'grounded', 'auditable'],
        variant: 'pill'
      }
    ],
    special: {
      type: 'examples',
      heading: 'Native to the system: complex, multi-clause reasoning',
      items: [
        '"Approve this claim only if the patient completed at least 6 PT sessions in the last 60 days, unless the referring physician documents progressive neurological deficits."',
        '"Evaluate whether this transaction violates any cross-regional compliance rules when combined with the client\u2019s historical behavior patterns."',
        '"Determine whether the recommended treatment pathway aligns with policy version 2025-B, considering comorbidities and medication contraindications."'
      ]
    }
  },
  {
    id: 'control',
    num: '05',
    name: 'Control',
    descriptor: 'Edit, inspect, override',
    title: 'Precisely Edit and Control the Cognitive Models',
    tagline: 'Full transparency. Full control. No black boxes.',
    intro:
      'Every semantic structure the engine generates is open to inspection and surgical change. You adjust thresholds, rule logic, constraints, exceptions, semantic relations, probabilistic weights, context sensitivities, and workflow branching directly \u2014 without retraining and without breaking the rest of the model.',
    whyItMatters:
      'You finally get an AI system experts can shape, compliance can validate, auditors can trace, developers can build on, and executives can trust.',
    chipGroups: [
      {
        label: 'Every semantic structure is',
        items: ['editable', 'inspectable', 'overrideable', 'mergeable', 'extendable', 'modular'],
        variant: 'pill'
      },
      {
        label: 'Surgical knobs you control',
        items: [
          'thresholds', 'rule logic', 'constraints', 'exceptions',
          'semantic relations', 'probabilistic weights',
          'context sensitivities', 'workflow branching'
        ],
        variant: 'chip'
      },
      {
        label: 'An AI system where',
        items: [
          'Experts shape it', 'Compliance validates', 'Auditors trace',
          'Developers build', 'Executives trust'
        ],
        variant: 'pill'
      }
    ],
    special: null
  },
  {
    id: 'federate',
    num: '06',
    name: 'Federate',
    descriptor: 'Layer boundaries & domains',
    title: 'Architect Layered, Federated Knowledge Domains',
    tagline: 'Global intelligence when needed. Scoped specialization when required.',
    intro:
      'CerebralMachines lets you architect knowledge the way a modern enterprise actually operates: as a federation of expertise, governed hierarchically, capable of merging on demand. You are not forced into one monolithic graph \u2014 you choose what is shared, what is scoped, and how layers inherit, override, and combine.',
    whyItMatters:
      'A cognitive model as structured as your enterprise, yet as fluid as human reasoning. No other system gives you this level of structural control.',
    chipGroups: [
      {
        label: 'Four layer types, one architecture',
        items: ['Enterprise', 'Department', 'Domain-specific', 'Project'],
        variant: 'pill'
      },
      {
        label: 'Layer interaction modes',
        items: [
          'Isolated', 'Connected on demand', 'Inherit + override',
          'Locked compliance', 'Local overrides'
        ],
        variant: 'pill'
      }
    ],
    special: {
      type: 'layerMatrix',
      heading: 'Four semantic layer types, each with its own job',
      layers: [
        {
          name: 'Enterprise Layer',
          bestFor: 'Core universal policies shared across the organization',
          example: 'Risk policies, compliance rules, global eligibility criteria'
        },
        {
          name: 'Department Layers',
          bestFor: 'Localized specialization, operational nuance',
          example: 'Claims rules, clinical policies, HR compliance, underwriting logic'
        },
        {
          name: 'Domain-Specific Layers',
          bestFor: 'Functionally deep expertise models',
          example: 'Cardiology vs Oncology rules, Tax law vs AML regulations'
        },
        {
          name: 'Temporary / Project Layers',
          bestFor: 'Experimental or transitional knowledge',
          example: 'Pilot programs, policy revisions, A/B logic structures'
        }
      ],
      useCasesHeading: 'Where layer boundaries become game-changing',
      useCases: [
        'A global insurer keeps a universal medical-necessity rule set with departmental variations for region-specific coverage.',
        'A health network holds enterprise-wide clinical safety policies while cardiology, neurology, and oncology run specialized rule layers.',
        'A bank enforces global AML requirements while local regulatory adaptations plug in at the country or product level.',
        'An enterprise sandboxes experimental logic without affecting production-grade knowledge.',
        'M&A integration becomes seamless \u2014 two semantic stacks connect rather than rebuild.'
      ]
    }
  },
  {
    id: 'govern',
    num: '07',
    name: 'Govern LLMs',
    descriptor: 'LLM as language interface',
    title: 'Govern Your LLMs \u2014 Make Them Safe, Compliant, Deterministic',
    tagline: 'Harness the power of LLMs with enterprise-grade precision and safety.',
    intro:
      'Large Language Models are extraordinary linguistic engines, but they are not decision engines. CerebralMachines becomes the governor, the supervisor, and the truth source: the semantic model decides the answer, the LLM only expresses it. Hallucinations stop because the LLM is not allowed to invent information outside the graph.',
    whyItMatters:
      'You finally deploy LLMs at scale with confidence \u2014 fluent, compliant, auditable sentence by sentence.',
    chipGroups: [
      {
        label: 'Where governed LLMs go to work',
        items: [
          'Explanations grounded in your rules',
          'Patient/customer-friendly summaries from verified facts',
          'Expert logic translated to human-readable decisions',
          'Strict boundaries on non-compliant outputs',
          'Total auditability \u2014 every sentence traces back to the graph'
        ],
        variant: 'pill'
      }
    ],
    special: {
      type: 'llmComparison',
      heading: 'LLM alone vs LLM on CerebralMachines',
      callout: 'A language interface \u2014 not a decision-maker.',
      before: {
        label: 'LLM alone',
        items: [
          'Hallucinates plausible-sounding answers',
          'Drifts run-to-run on identical inputs',
          'Contradicts internal policy',
          'Untrusted in mission-critical paths',
          'Audit trail = a free-text rationale'
        ]
      },
      after: {
        label: 'LLM on CerebralMachines',
        items: [
          'Bounded by the semantic graph',
          'Cites the exact rule, clause, or evidence',
          'Deterministic decisioning underneath',
          'Auditable sentence by sentence',
          'Quality, trust, and compliance enforced before it ever speaks'
        ]
      }
    }
  }
];

/* ================================================================
   BRIEFING DATA -- 60-second decision-maker briefing at top of page
   ================================================================ */
const briefingQA = [
  {
    q: 'Why is enterprise AI hitting a wall?',
    a: 'Most enterprise AI initiatives today \u2014 whether on Microsoft Azure, Amazon Web Services, or Google Cloud Platform \u2014 run into the same wall:',
    pointsStyle: 'pain',
    points: [
      'Unpredictable outputs',
      'Rising costs',
      'Slow, fragile workflows',
      'Limited control and auditability'
    ]
  },
  {
    q: 'How we solve it',
    points: [
      { text: 'Solves these issues at the core of your AI stack' },
      {
        highlight: true,
        text: (
          <>
            Introduces a new class of AI architecture &mdash; the{' '}
            <strong className="hiw-qa__hcole-name">Hybrid Cognitive Linguistic Core (HCoLE)</strong>
            {' '}&mdash; a deterministic reasoning layer that fixes all 6 core issues
          </>
        )
      },
      { text: 'Augments LLMs to make workflows predictable, auditable, faster, and more cost-efficient' },
      { text: 'Does not replace your cloud, data, or security architecture' }
    ]
  },
  {
    q: 'How does it integrate?',
    points: [
      { text: 'Enterprise AI platform for end-to-end workflow execution' },
      { text: 'Replaces indexing, search, and execution components' },
      {
        text: 'Your workflows call CerebralMachines APIs instead of:',
        sublist: [
          'Azure Search / Azure AI Foundry',
          'AWS / GCP search, retrieval, and execution services'
        ]
      },
      { text: 'Simplifies complex AI into a controlled, reliable system' },
      { text: 'Includes full-spectrum AI management tools for turnkey execution' }
    ]
  },
  {
    q: 'My Existing AI Flows?',
    a: 'Yes \u2014 integrates with both existing and new workflows.',
    pointsLead: 'Delivers immediate, measurable improvements:',
    points: [
      { metric: { label: 'Accuracy',       before: '30\u201370%',  after: '98%' } },
      { metric: { label: 'Performance',    after: '4x faster' } },
      { metric: { label: 'Consistency',    before: '60\u201380%',  after: '98%' } },
      {
        metric: {
          label: 'Cost',
          after: 'Cut LLM inference tokens by up to 70%',
          note: 'Potentially saving millions per month for large implementations'
        }
      },
      { metric: { label: 'Explainability', before: 'Poor',          after: 'Excellent' } },
      { metric: { label: 'Auditability',   before: 'Poor',          after: 'Excellent' } },
      {
        metric: {
          label: 'Control',
          before: 'Poor',
          after: 'Full control',
          note: 'Define and engineer the exact AI behavior \u2014 no black boxes any more'
        }
      }
    ]
  },
  {
    q: 'What does it cost?',
    points: [
      '$0 core POC to prove your use case',
      'Annual license for enterprise-wide deployment',
      'Advanced engineering services for integrations and customizations'
    ]
  },
  {
    q: 'How quickly can I evaluate it?',
    points: [
      'Core POC in days',
      'Integrated POC in weeks'
    ]
  },
  {
    q: 'Can I see this in action?',
    a: 'Yes \u2014 short walkthroughs and live demonstrations on real use cases available.',
    ctas: [
      { label: 'Request a Demo', to: '/request-demo' },
      { label: 'Review Use Cases', to: '/use-cases' }
    ]
  },
  {
    q: 'Is this established?',
    points: [
      'Extensively tested across multiple domains with complex use cases',
      'Consistently delivers strong, reliable results',
      'Solves critical enterprise AI issues in a turnkey model'
    ]
  },
  {
    q: 'What about risk, security, and scale?',
    points: [
      'Runs within your existing cloud environment',
      'Aligns with enterprise security controls',
      'Scales with increasing workflow complexity'
    ]
  },
  {
    q: 'Is this relevant for you?',
    a: 'If your AI is struggling with:',
    points: [
      'Accuracy',
      'Performance',
      'Consistency',
      'Cost'
    ],
    footer: 'Then CerebralMachines.ai provides a fundamentally stronger approach \u2014 delivering control, reliability, and predictability so your AI can survive operational pressure and thrive at enterprise scale.'
  }
];

/* ================================================================
   PRIMITIVES
   ================================================================ */

function ChipCloud({ label, items, variant }) {
  const cls =
    variant === 'pill' ? 'hiw-pill-row'
    : variant === 'negative' ? 'hiw-pill-row hiw-pill-row--negative'
    : 'hiw-chips';
  const itemCls =
    variant === 'pill' ? 'hiw-pill'
    : variant === 'negative' ? 'hiw-pill hiw-pill--negative'
    : 'hiw-chip';

  return (
    <div className="hiw-chip-group">
      {label && <div className="hiw-chip-group__label">{label}</div>}
      <div className={cls}>
        {items.map((item, i) => (
          <span key={i} className={itemCls}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function ExampleGrid({ heading, items }) {
  return (
    <div className="hiw-special hiw-examples-block">
      {heading && <h4 className="hiw-special__heading">{heading}</h4>}
      <div className="hiw-examples-grid">
        {items.map((item, i) => (
          <article key={i} className="hiw-example-card">
            <span className="hiw-example-card__quote-mark" aria-hidden="true">&ldquo;</span>
            <p>{item.replace(/^"|"$/g, '')}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

function LayerMatrix({ heading, layers, useCasesHeading, useCases }) {
  return (
    <div className="hiw-special hiw-layer-block">
      {heading && <h4 className="hiw-special__heading">{heading}</h4>}
      <div className="hiw-layer-matrix">
        {layers.map((layer, i) => (
          <article key={i} className="hiw-layer-card">
            <div className="hiw-layer-card__num">{String(i + 1).padStart(2, '0')}</div>
            <h5 className="hiw-layer-card__name">{layer.name}</h5>
            <div className="hiw-layer-card__row">
              <span className="hiw-layer-card__row-label">Best for</span>
              <span className="hiw-layer-card__row-text">{layer.bestFor}</span>
            </div>
            <div className="hiw-layer-card__row">
              <span className="hiw-layer-card__row-label">Example scope</span>
              <span className="hiw-layer-card__row-text">{layer.example}</span>
            </div>
          </article>
        ))}
      </div>

      {useCasesHeading && <h4 className="hiw-special__heading hiw-special__heading--sub">{useCasesHeading}</h4>}
      <ul className="hiw-layer-uses">
        {useCases.map((uc, i) => (
          <li key={i} className="hiw-layer-uses__item">
            <span className="hiw-layer-uses__check" aria-hidden="true">&#10003;</span>
            <span>{uc}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LlmComparison({ heading, callout, before, after }) {
  return (
    <div className="hiw-special hiw-llm-compare">
      {heading && <h4 className="hiw-special__heading">{heading}</h4>}
      <div className="hiw-llm-compare__grid">
        <section className="hiw-llm-col hiw-llm-col--before">
          <div className="hiw-llm-col__tag">{before.label}</div>
          <ul className="hiw-llm-col__list">
            {before.items.map((it, i) => (
              <li key={i}><span className="hiw-llm-col__icon" aria-hidden="true">&times;</span>{it}</li>
            ))}
          </ul>
        </section>
        <section className="hiw-llm-col hiw-llm-col--after">
          <div className="hiw-llm-col__tag">{after.label}</div>
          <ul className="hiw-llm-col__list">
            {after.items.map((it, i) => (
              <li key={i}><span className="hiw-llm-col__icon" aria-hidden="true">&#10003;</span>{it}</li>
            ))}
          </ul>
        </section>
      </div>
      {callout && <div className="hiw-llm-callout">{callout}</div>}
    </div>
  );
}

function PipelineOverview({ steps, onJump }) {
  return (
    <div className="hiw-pipeline">
      <div className="hiw-pipeline__row">
        {steps.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              type="button"
              className="hiw-pipeline__node"
              onClick={() => onJump(s.id)}
            >
              <span className="hiw-pipeline__node-num">{s.num}</span>
              <span className="hiw-pipeline__node-name">{s.name}</span>
              <span className="hiw-pipeline__node-desc">{s.descriptor}</span>
            </button>
            {i < steps.length - 1 && (
              <span className="hiw-pipeline__chevron" aria-hidden="true">&rarr;</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function StickyStepper({ steps, activeId, onJump }) {
  return (
    <div className="hiw-stepper-wrap">
      <div className="hiw-stepper">
        <div className="hiw-stepper__inner">
          {steps.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`hiw-stepper__btn ${activeId === s.id ? 'hiw-stepper__btn--active' : ''}`}
              onClick={() => onJump(s.id)}
            >
              <span className="hiw-stepper__num">{s.num}</span>
              <span className="hiw-stepper__name">{s.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepPanel({ step, index, total }) {
  const isLast = index === total - 1;
  const nextLabel = !isLast ? `${steps[index + 1].num} \u00b7 ${steps[index + 1].name}` : null;

  return (
    <motion.section
      id={`step-${step.id}`}
      className={`hiw-step ${index % 2 === 1 ? 'hiw-step--alt' : ''} ${isLast ? 'hiw-step--last' : ''}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6 }}
    >
      <div className="hiw-container">
        <header className="hiw-step__head">
          <div className="hiw-step__badge">
            <span className="hiw-step__badge-num">{step.num}</span>
            <span className="hiw-step__badge-sep">/</span>
            <span className="hiw-step__badge-total">{String(total).padStart(2, '0')}</span>
            <span className="hiw-step__badge-dot" aria-hidden="true">&bull;</span>
            <span className="hiw-step__badge-name">{step.name.toUpperCase()}</span>
          </div>
          <h2 className="hiw-step__title">{step.title}</h2>
          <p className="hiw-step__tagline">{step.tagline}</p>
        </header>

        <div className="hiw-step__grid">
          <div className="hiw-step__left">
            <div className="hiw-step__intro-label">What it means</div>
            <p className="hiw-step__intro">{step.intro}</p>
            <div className="hiw-step__why">
              <span className="hiw-step__why-label">Why it matters</span>
              <p>{step.whyItMatters}</p>
            </div>
          </div>

          <div className="hiw-step__right">
            {step.chipGroups.map((g, i) => (
              <ChipCloud key={i} label={g.label} items={g.items} variant={g.variant} />
            ))}
          </div>
        </div>

        {step.special && step.special.type === 'examples' && (
          <ExampleGrid heading={step.special.heading} items={step.special.items} />
        )}
        {step.special && step.special.type === 'layerMatrix' && (
          <LayerMatrix
            heading={step.special.heading}
            layers={step.special.layers}
            useCasesHeading={step.special.useCasesHeading}
            useCases={step.special.useCases}
          />
        )}
        {step.special && step.special.type === 'llmComparison' && (
          <LlmComparison
            heading={step.special.heading}
            callout={step.special.callout}
            before={step.special.before}
            after={step.special.after}
          />
        )}

        {!isLast && nextLabel && (
          <div className="hiw-step__connector" aria-hidden="true">
            <span className="hiw-step__connector-line"></span>
            <span className="hiw-step__connector-label">Next &middot; {nextLabel}</span>
            <span className="hiw-step__connector-arrow">&darr;</span>
          </div>
        )}
      </div>
    </motion.section>
  );
}

function ClosingSummary({ steps }) {
  return (
    <section className="hiw-summary">
      <div className="hiw-container">
        <motion.div {...fadeInUp} className="hiw-summary__inner">
          <span className="hiw-eyebrow">THE COGNITIVE BACKBONE</span>
          <h2 className="hiw-summary__title">All Seven Steps Are One Platform</h2>
          <p className="hiw-summary__lede">
            Build, connect, live, invoke, control, federate, govern &mdash; every step above runs on the same backbone.
            One unified cognitive model. One source of truth for every AI surface in your enterprise.
          </p>
          <div className="hiw-summary__steps">
            {steps.map((s) => (
              <span key={s.id} className="hiw-summary__chip">
                <span className="hiw-summary__chip-num">{s.num}</span>
                <span className="hiw-summary__chip-name">{s.name}</span>
              </span>
            ))}
          </div>
          <div className="hiw-summary__arrow" aria-hidden="true">&darr;</div>
          <div className="hiw-summary__platform">
            <span className="hiw-summary__platform-label">Powered by</span>
            <span className="hiw-summary__platform-name">Hybrid Cognitive Linguistic Core (HCLC)</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ================================================================
   PAGE
   ================================================================ */

/* ================================================================
   BRIEFING COMPONENTS
   ================================================================ */

function BriefingQAList({ items }) {
  return (
    <div className="hiw-qa-block">
      <ol className="hiw-qa">
        {items.map((it, idx) => {
          const listClasses = ['hiw-qa__list'];
          if (it.pointsStyle === 'pain') listClasses.push('hiw-qa__list--pain');
          return (
            <li key={idx} className="hiw-qa__item">
              <h2 className="hiw-qa__q">{it.q}</h2>
              <div className="hiw-qa__body">
                {it.a && <p className="hiw-qa__a">{it.a}</p>}
                {it.pointsLead && <p className="hiw-qa__points-lead">{it.pointsLead}</p>}
                {it.points && (
                  <ul className={listClasses.join(' ')}>
                    {it.points.map((p, i) => {
                      if (typeof p === 'string') {
                        return <li key={i}>{p}</li>;
                      }
                      if (p.metric) {
                        const { label, before, after, note } = p.metric;
                        return (
                          <li key={i} className="hiw-qa__list-item hiw-qa__list-item--metric">
                            <div className="hiw-qa__metric">
                              <span className="hiw-qa__metric-label">{label}</span>
                              <div className="hiw-qa__metric-value-col">
                                <span className="hiw-qa__metric-headline">
                                  {before && (
                                    <>
                                      <span className="hiw-qa__metric-before">{before}</span>
                                      <span className="hiw-qa__metric-arrow" aria-hidden="true">&rarr;</span>
                                    </>
                                  )}
                                  {after && <span className="hiw-qa__metric-after">{after}</span>}
                                </span>
                                {note && <span className="hiw-qa__metric-note">{note}</span>}
                              </div>
                            </div>
                          </li>
                        );
                      }
                      const itemClasses = ['hiw-qa__list-item'];
                      if (p.highlight) itemClasses.push('hiw-qa__list-item--highlight');
                      return (
                        <li key={i} className={itemClasses.join(' ')}>
                          <span className="hiw-qa__list-text">{p.text}</span>
                          {p.sublist && (
                            <ul className="hiw-qa__sublist">
                              {p.sublist.map((sub, j) => (
                                <li key={j} className="hiw-qa__sublist-item">{sub}</li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {it.footer && <p className="hiw-qa__footer">{it.footer}</p>}
                {it.ctas && (
                  <div className="hiw-qa__cta-row">
                    {it.ctas.map((c, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="hiw-qa__cta-join">or</span>}
                        <Link to={c.to} className="hiw-qa__cta">
                          {c.label}
                          <span className="hiw-qa__cta-arrow" aria-hidden="true">&rarr;</span>
                        </Link>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function BriefingSection() {
  return (
    <section className="hiw-brief">
      <div className="hiw-container">
        <motion.div {...fadeInUp}>
          <span className="hiw-brief__eyebrow">Decision Maker&rsquo;s 60-Second Briefing</span>
          <h1 className="hiw-brief__title">
            What this means for your enterprise AI &mdash; quickly and clearly.
          </h1>
          <p className="hiw-brief__sublede">
            CerebralMachines.ai solves accuracy, performance, reliability, and cost issues for enterprise AI &mdash; especially for complex use cases involving large volumes of documents in policy and regulatory environments.
          </p>
        </motion.div>

        <motion.div {...fadeInUp}>
          <BriefingQAList items={briefingQA} />
        </motion.div>
      </div>
    </section>
  );
}

function BroaderPlayDivider() {
  return (
    <section className="hiw-broader">
      <div className="hiw-container">
        <span className="hiw-broader__eyebrow">The broader enterprise play</span>
        <h2 className="hiw-broader__title">Going deeper: the cognitive enterprise brain in seven steps.</h2>
        <p className="hiw-broader__lede">
          The briefing above is the buyer&rsquo;s read. Below is the architect&rsquo;s read &mdash; how plain-English authoring becomes a production reasoning core, step by step.
        </p>
      </div>
    </section>
  );
}

function HowItWorks() {
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState(steps[0].id);
  const navigate = useNavigate();
  const observerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          const id = visible[0].target.id.replace(/^step-/, '');
          setActiveId(id);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    observerRef.current = observer;

    steps.forEach((s) => {
      const el = document.getElementById(`step-${s.id}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleJump = useCallback((id) => {
    const el = document.getElementById(`step-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div className="how-it-works-page">
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting">Consulting</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
      </nav>

      {/* 60-Second Briefing -- new top of page */}
      <BriefingSection />

      {/* Divider into the broader enterprise play */}
      <BroaderPlayDivider />

      {/* Hero (existing -- demoted from h1 to h2) */}
      <section className="hiw-hero">
        <div className="hiw-container">
          <motion.div {...fadeInUp}>
            <span className="hiw-hero-eyebrow">HOW IT WORKS</span>
            <h2 className="hiw-main-title">
              From Plain English to a Cognitive Enterprise Brain &mdash; in Seven Steps
            </h2>
            <p className="hiw-hero-lede">
              CerebralMachines replaces the maze of disconnected tools &mdash; RAG, vector search, rules engines,
              fine-tuned models, workflow orchestrators &mdash; with a single cognitive layer that understands your
              domain the way your best experts do. The seven steps below show how it goes from plain English
              to production reasoning.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pipeline Overview */}
      <section className="hiw-pipeline-section">
        <div className="hiw-container">
          <motion.div {...fadeInUp}>
            <PipelineOverview steps={steps} onJump={handleJump} />
          </motion.div>
        </div>
      </section>

      {/* Sticky Stepper */}
      <StickyStepper steps={steps} activeId={activeId} onJump={handleJump} />

      {/* Step Panels */}
      <div className="hiw-steps">
        {steps.map((s, i) => (
          <StepPanel key={s.id} step={s} index={i} total={steps.length} />
        ))}
      </div>

      {/* Closing Summary */}
      <ClosingSummary steps={steps} />

      {/* CTA Section */}
      <section className="hiw-cta">
        <div className="hiw-container">
          <motion.div {...fadeInUp} className="hiw-cta-content">
            <h2 className="hiw-cta-title">Ready to Build Your Enterprise Brain?</h2>
            <p className="hiw-cta-text">
              See how CerebralMachines transforms your AI strategy with deterministic reasoning,
              full explainability, and a single cognitive backbone for every AI surface.
            </p>
            <div className="hiw-cta-buttons">
              <button className="btn-primary-large" onClick={() => navigate('/request-demo')}>Request a Demo</button>
              <button className="btn-secondary-large" onClick={() => navigate('/use-cases')}>See it in action</button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">
                Building the cognitive backbone for enterprise AI.
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Product</h4>
                <ul>
                  <li><a href="/platform">Platform</a></li>
                  <li><a href="/how-it-works">How it Works</a></li>
                  <li><a href="/use-cases">Use Cases</a></li>
                  <li><a href="/consulting">Consulting</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Company</h4>
                <ul>
                  <li><a href="/about">About</a></li>
                  <li><a href="/team">Team</a></li>
                  <li><a href="/careers">Careers</a></li>
                  <li><a href="/contact">Contact</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Legal</h4>
                <ul>
                  <li><a href="/privacy">Privacy Policy</a></li>
                  <li><a href="/terms">Terms of Service</a></li>
                  <li><a href="/security">Security</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HowItWorks;
